export const STATUS_META = {
  attended: { label: 'Attended', variant: 'active' },
  late: { label: 'Late', variant: 'warning' },
  absent: { label: 'Absent', variant: 'danger' },
  unauthorised: { label: 'Unauthorised', variant: 'danger' }
};

const statusAliases = {
  attended: 'attended',
  present: 'attended',
  late: 'late',
  'partially attended': 'late',
  absent: 'absent',
  'authorised absence': 'absent',
  disrupted: 'absent',
  'session stopped': 'absent',
  unauthorised: 'unauthorised',
  'unauthorised absence': 'unauthorised',
  'unauthorized absence': 'unauthorised'
};

export const normalizeAttendanceStatus = (status) => {
  if (!status) return 'attended';
  const key = status.toString().trim().toLowerCase();
  return statusAliases[key] || 'attended';
};

export const getStatusMeta = (status) => {
  const normalized = normalizeAttendanceStatus(status);
  return STATUS_META[normalized] || STATUS_META.attended;
};

export const isExceptionStatus = (status) => normalizeAttendanceStatus(status) !== 'attended';

export const isSafeguardingFlagged = (student) => {
  if (!student) return false;
  const events = [
    ...(student.safeguardingEvents || []),
    ...(student.medicalEvents || []),
    ...(student.policeEvents || [])
  ];
  return events.some((event) => {
    if (!event) return false;
    const status = (event.status || '').toLowerCase();
    const severity = (event.severity || '').toLowerCase();
    return status === 'open' || status === 'monitoring' || severity === 'high' || severity === 'critical';
  });
};

export const buildAttendanceKpis = (students, threshold = 70) => {
  const total = students.length;
  const average = total
    ? Math.round(students.reduce((sum, s) => sum + (s.attendance?.rate || 0), 0) / total)
    : 0;
  const below70 = students.filter((s) => (s.attendance?.rate || 0) < threshold).length;
  const below90 = students.filter((s) => (s.attendance?.rate || 0) < 90).length;
  const flagged = students.filter((s) => isSafeguardingFlagged(s)).length;
  return { total, average, below70, below90, flagged };
};

const toDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const getAttendanceSignals = (student) => {
  if (!student) return [];
  const logs = (student.attendance?.logs || [])
    .map((log) => ({
      ...log,
      status: normalizeAttendanceStatus(log.status),
      dateValue: toDate(log.recordedAt || log.date)
    }))
    .filter((log) => log.dateValue)
    .sort((a, b) => b.dateValue - a.dateValue);

  const signals = [];
  const attendanceRate = student.attendance?.rate ?? 100;

  const lastThree = logs.slice(0, 3);
  if (
    lastThree.length === 3 &&
    lastThree.every((log) => log.status !== 'attended') &&
    attendanceRate < 85
  ) {
    signals.push({
      id: 'low-consecutive',
      level: 'high',
      text: 'Attendance below 85% for 3 consecutive sessions'
    });
  }

  const fourteenDaysAgo = new Date();
  fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
  const unauthorisedCount = logs.filter(
    (log) => log.status === 'unauthorised' && log.dateValue >= fourteenDaysAgo
  ).length;
  if (unauthorisedCount >= 2) {
    signals.push({
      id: 'unauthorised-14',
      level: 'critical',
      text: '2 unauthorised absences in last 14 days'
    });
  }

  return signals;
};
